import {omneticApi} from '@dms/api/base';

import {
  AddTeamMembersApiResponse,
  AddTeamMembersApiArg,
  AddTeamMembersApiArgSchema,
  AddTeamMembersApiResponseSchema,
  DeactivateTeamMemberApiResponse,
  DeactivateTeamMemberApiArg,
  DeactivateTeamMemberApiArgSchema,
  DeactivateTeamMemberApiResponseSchema,
  CreateTeamApiResponse,
  CreateTeamApiArg,
  CreateTeamApiArgSchema,
  CreateTeamApiResponseSchema,
  DeleteTeamApiResponse,
  DeleteTeamApiArg,
  DeleteTeamApiArgSchema,
  DeleteTeamApiResponseSchema,
  GetTeamApiResponse,
  GetTeamApiArg,
  GetTeamApiArgSchema,
  GetTeamApiResponseSchema,
  UpdateTeamApiResponse,
  UpdateTeamApiArg,
  UpdateTeamApiArgSchema,
  UpdateTeamApiResponseSchema,
  DeleteTeamMemberApiResponse,
  DeleteTeamMemberApiArg,
  DeleteTeamMemberApiArgSchema,
  DeleteTeamMemberApiResponseSchema,
  ActivateTeamMemberApiResponse,
  ActivateTeamMemberApiArg,
  ActivateTeamMemberApiArgSchema,
  ActivateTeamMemberApiResponseSchema,
  ListTeamsApiResponse,
  ListTeamsApiArg,
  ListTeamsApiArgSchema,
  ListTeamsApiResponseSchema,
} from './types';

const teamApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    addTeamMembers: build.mutation<AddTeamMembersApiResponse, AddTeamMembersApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.teamId}/member`,
        method: 'POST',
        body: queryArg.addTeamMembersRequestBody,
      }),
      extraOptions: {
        requestSchema: AddTeamMembersApiArgSchema,
        responseSchema: AddTeamMembersApiResponseSchema,
      },
    }),
    deactivateTeamMember: build.mutation<
      DeactivateTeamMemberApiResponse,
      DeactivateTeamMemberApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.teamId}/member/${queryArg.memberId}/deactivate`,
        method: 'POST',
      }),
      extraOptions: {
        requestSchema: DeactivateTeamMemberApiArgSchema,
        responseSchema: DeactivateTeamMemberApiResponseSchema,
      },
    }),
    createTeam: build.mutation<CreateTeamApiResponse, CreateTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team`,
        method: 'POST',
        body: queryArg.createTeamRequestBody,
      }),
      invalidatesTags: [{type: 'Team', id: 'LIST'}],
      extraOptions: {
        requestSchema: CreateTeamApiArgSchema,
        responseSchema: CreateTeamApiResponseSchema,
      },
    }),
    deleteTeam: build.mutation<DeleteTeamApiResponse, DeleteTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{type: 'Team', id: 'LIST'}],
      extraOptions: {
        requestSchema: DeleteTeamApiArgSchema,
        responseSchema: DeleteTeamApiResponseSchema,
      },
    }),
    getTeam: build.query<GetTeamApiResponse, GetTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.id}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'Team', id: queryArg.id}],
      extraOptions: {
        requestSchema: GetTeamApiArgSchema,
        responseSchema: GetTeamApiResponseSchema,
      },
    }),
    listTeams: build.query<ListTeamsApiResponse, ListTeamsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team`,
        params: {
          ...(queryArg.active !== undefined && {active: queryArg.active}),
          ...(queryArg.branchId !== undefined && {branchId: queryArg.branchId}),
        },
      }),
      providesTags: [{type: 'Team', id: 'LIST'}],
      extraOptions: {
        requestSchema: ListTeamsApiArgSchema,
        responseSchema: ListTeamsApiResponseSchema,
      },
    }),
    updateTeam: build.mutation<UpdateTeamApiResponse, UpdateTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.id}`,
        method: 'PUT',
        body: queryArg.updateTeamRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'Team', id: queryArg.id},
        {type: 'Team', id: 'LIST'},
      ],
      extraOptions: {
        requestSchema: UpdateTeamApiArgSchema,
        responseSchema: UpdateTeamApiResponseSchema,
      },
    }),
    deleteTeamMember: build.mutation<DeleteTeamMemberApiResponse, DeleteTeamMemberApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.teamId}/member/${queryArg.memberId}`,
        method: 'DELETE',
      }),
      extraOptions: {
        requestSchema: DeleteTeamMemberApiArgSchema,
        responseSchema: DeleteTeamMemberApiResponseSchema,
      },
    }),
    activateTeamMember: build.mutation<ActivateTeamMemberApiResponse, ActivateTeamMemberApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/team/${queryArg.teamId}/member/${queryArg.memberId}/activate`,
        method: 'POST',
      }),
      extraOptions: {
        requestSchema: ActivateTeamMemberApiArgSchema,
        responseSchema: ActivateTeamMemberApiResponseSchema,
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddTeamMembersMutation,
  useDeactivateTeamMemberMutation,
  useCreateTeamMutation,
  useDeleteTeamMutation,
  useGetTeamQuery,
  useListTeamsQuery,
  useUpdateTeamMutation,
  useDeleteTeamMemberMutation,
  useActivateTeamMemberMutation,
} = teamApi;
